import {jsx} from 'panel';
import pick from 'lodash/pick';
import {ParamComponent} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {ButtonAttrs, ButtonEl, buttonParams} from 'common/components/button';

import {SelectEl} from '../select';
import {ChangeEvent, SelectElProps} from '../select/types';
import {Directions} from '../drop-menu/types';
import css from './index.inline.styl';

type SplitButtonParams = SelectElProps['props'] &
  ButtonAttrs & {
    handleClick?: (ev: MouseEvent) => void;
    handleSelectChange?: (ev: ChangeEvent) => void;
  };

export interface SplitButtonJsxProps {
  params?: SplitButtonParams;
  on?: {
    click?: (ev: MouseEvent) => void;
    change?: (ev: ChangeEvent) => void;
  };
}

export class SplitButton extends ParamComponent<SplitButtonParams, Obj> {
  get config() {
    return {
      css,
      useShadowDom: true,
      template: () => this.renderTemplate(),
      defaultParams: {
        disabled: false,
      },
      params: {
        ...buttonParams,
        disabled: {
          type: Boolean,
          default: false,
          required: false,
        },
        handleClick: Function,
        handleSelectChange: Function,
        sections: Array,
        sectionsFn: Function,
        sectionsFromInputFn: Function,
        dropdownBodyRenderer: Function,
        dropdownPositionParent: Object,
        contextPanelPositionParentElement: Object,
        contextPanelTransform: Function,
      },
    };
  }

  renderTemplate() {
    const hasOptions = Array.isArray(this.params.sections) && this.params.sections.length > 0;

    return (
      <div
        sel='.mp-split-button'
        class={{
          'has-options': hasOptions,
        }}
      >
        <ButtonEl
          attrs={{
            ...pick(this.params, Object.keys(buttonParams)),
            elref: `primaryButton`,
          }}
          on={{click: this.handleClick}}
        >
          {this.innerHTML.trim()}
        </ButtonEl>

        {/* render options select only when there are available options */}
        {hasOptions && (
          <SelectEl
            attrs={{
              elref: `selectButton`,
              disabled: this.params.disabled,
              'show-icon-only-down-caret': true,
              'always-show-caret': true,
              icon: {
                rightIcon: `caret-down`,
              },
              theme: this.params.theme as any,
              'menu-width': 0,
              'menu-height': null,
              'menu-alignment': Directions.DownRight,
            }}
            props={{
              sections: this.params.sections,
              sectionsFn: this.params.sectionsFn,
              sectionsFromInputFn: this.params.sectionsFromInputFn,
              dropdownPositionParent: this,
              dropdownBodyRenderer: this.params.dropdownBodyRenderer,
              contextPanelTransform: this.params.contextPanelTransform,
              contextPanelPositionParentElement: this.params.contextPanelPositionParentElement,
            }}
            on={{
              change: this.handleSelectChange,
            }}
          />
        )}
      </div>
    );
  }

  handleClick = (ev: MouseEvent) => {
    if (this.params.disabled === false && this.params.handleClick) {
      ev.preventDefault();
      ev.stopImmediatePropagation();
      this.params.handleClick(ev);
    }
  };

  handleSelectChange = (ev: ChangeEvent) => {
    if (this.params.disabled === false && this.params.handleSelectChange) {
      this.params.handleSelectChange(ev);
    }
  };
}

export const SplitButtonEl = defineAndMakeJsxFactory<SplitButtonJsxProps>(`mp-split-button`, SplitButton);
export const SplitButtonReact = panel2react<SplitButtonJsxProps>(`mp-split-button`, SplitButton);
